import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, ButtonSize, Show } from "shared/components";
import { Loader } from "shared/components/Loader";
import { lockboxesApi } from "api/landlord/lockboxes";
import type { AccessCode, Lockbox } from "api/landlord/lockboxes";
import { VENDOR_LABELS } from "views/lockboxes/constants";
import { GenerateLockboxCodeModal } from "./GenerateLockboxCodeModal";
import { CancelLockboxCodeDialog } from "./CancelLockboxCodeDialog";
import { LockboxCodeRow } from "./LockboxCodeRow";
import {
  codeBoundaries,
  resolveCodeTimezone,
  visibleCodes
} from "./codeScheduling";
import { accessCodesQueryKey } from "./queryKeys";
import { useNow } from "./useNow";

export const LockboxCodeGenerator = ({
  lockbox,
  timezone,
  assignAccountHint,
  actionLabels = "full"
}: {
  lockbox: Lockbox;
  timezone?: string | null;
  assignAccountHint: string;
  actionLabels?: "full" | "icon-copy";
}) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [codeToCancel, setCodeToCancel] = useState<AccessCode | null>(null);

  const { data, isPending, isError, refetch, isRefetching } = useQuery({
    queryKey: accessCodesQueryKey(lockbox.id),
    queryFn: () => lockboxesApi.listAccessCodes(lockbox.id)
  });

  const allCodes = data?.access_codes;
  const boundaries = useMemo(() => codeBoundaries(allCodes ?? []), [allCodes]);
  const now = useNow({ boundaries });
  const codes = useMemo(
    () => visibleCodes(allCodes ?? [], now),
    [allCodes, now]
  );

  const codeTimezone = resolveCodeTimezone(
    lockbox.timezone ?? timezone ?? data?.time_zone
  );
  const vendorLabel = lockbox.vendor ? VENDOR_LABELS[lockbox.vendor] : "vendor";
  const missingVendorAccount = !lockbox.vendor_account;
  const loadFailed = isError && !data;
  const showList = codes.length > 0;
  const showEmptyState = !showList && !isPending && !loadFailed;

  return (
    <>
      <Show if={isPending}>
        <div className="flex items-center justify-center rounded-lg border border-dashed border-alpha-100 bg-background-900 p-6">
          <Loader size={24} />
        </div>
      </Show>

      <Show if={loadFailed}>
        <div className="flex items-center justify-between gap-2.5 rounded-lg border border-dashed border-alpha-100 bg-background-900 p-6">
          <p className="text-sm font-medium text-error-500">
            Couldn&apos;t load this lockbox&apos;s codes. Please try again.
          </p>
          <div className="flex shrink-0 gap-2">
            <Button
              size={ButtonSize.SM}
              loading={isRefetching}
              disabled={isRefetching}
              onClick={() => refetch()}
            >
              Try again
            </Button>
            <Button
              size={ButtonSize.SM}
              disabled={missingVendorAccount}
              onClick={() => setIsConfirming(true)}
            >
              Generate lockbox code
            </Button>
          </div>
        </div>
      </Show>

      <Show if={showList}>
        <div className="space-y-4 rounded-lg border border-dashed border-alpha-100 bg-background-900 p-6">
          <div className="divide-y divide-alpha-40">
            {codes.map((code) => (
              <LockboxCodeRow
                key={`${code.source}:${code.id}`}
                code={code}
                zone={codeTimezone.zone}
                now={now}
                actionLabels={actionLabels}
                onCancel={setCodeToCancel}
              />
            ))}
          </div>
          <Button
            size={ButtonSize.SM}
            disabled={missingVendorAccount}
            onClick={() => setIsConfirming(true)}
          >
            Generate another code
          </Button>
        </div>
      </Show>

      <Show if={showEmptyState}>
        <div className="flex items-center justify-between gap-2.5 rounded-lg border border-dashed border-alpha-100 bg-background-900 p-6">
          <div className="space-y-1">
            <p className="text-2xl font-bold text-content-1000">- - - -</p>
            <p className="text-sm font-medium text-content-400">
              The code will appear here
            </p>
          </div>
          <Button
            size={ButtonSize.SM}
            className="shrink-0"
            disabled={missingVendorAccount}
            onClick={() => setIsConfirming(true)}
          >
            Generate lockbox code
          </Button>
        </div>
      </Show>

      <Show if={missingVendorAccount}>
        <div className="rounded-lg bg-warning-50 p-4">
          <p className="text-sm text-warning-700 font-medium">
            Assign a {vendorLabel} account to this lockbox {assignAccountHint}{" "}
            to generate a code.
          </p>
        </div>
      </Show>

      <GenerateLockboxCodeModal
        opened={isConfirming}
        onClose={() => setIsConfirming(false)}
        onGenerated={() => setIsConfirming(false)}
        lockbox={lockbox}
        timezone={codeTimezone}
      />

      <CancelLockboxCodeDialog
        lockboxId={lockbox.id}
        code={codeToCancel}
        onClose={() => setCodeToCancel(null)}
      />
    </>
  );
};
